import firebase from 'firebase/app';
import 'firebase/database';
import { User, Role } from '../model/user';
import { Message } from '../model/message';
import {
    MessageConvertResponse,
} from "../../infra/api/service/message_api_convert";

class Member {
    public static fromRTDB(from: any): Member {
        return new Member(from.userId, from.name, from.iconUrl, from.iconServingUrl);
    }

    constructor(
        public readonly id: string, 
        public readonly name: string, 
        public readonly iconURL: string,
        public readonly iconServingURL: string) {

    }
}

interface IChatUseCase {
    connectRoom(user: User, projectID: string, opponentID: string): Promise<Message[]>;
    observeMessage(callback: (message: Message) => void): void;
    sendMessage(user: User, text: string, imageURL: string, fileURL: string, toId: string): Promise<void>;
    closeRoom(): any;
    getMembers(): Member[];
}

class ChatUseCase implements IChatUseCase {

    private roomID: string = "";
    private members: Member[] = [];
    private messages: Message[] = [];

    // Roomの初期化処理をします。
    // members, messagesを取得してメモリに保持しておきます。
    // メッセージのやり取りでユーザーのアイコン、ユーザー名を表示すると思いますが、それはこのmemberから引いて使ってください。
    // member以外はこのチャットを読み込み書き込みできないように制限。
    public connectRoom = (user: User, projectID: string, opponentID: string): Promise<Message[]> => {
        return new Promise<Message[]>((resolve, reject) =>{
            let roomID: string;
            switch (user.role) {
                case Role.Role_Customer:
                    if (!user.customer) {
                        throw Error();
                    }
                    roomID = projectID + "-" + user.customer.id + "-" + opponentID;
                    break;
                case Role.Role_Client:
                    if (!user.client) {
                        throw Error();
                    }
                    roomID = projectID + "-" + opponentID + "-" + user.client.companyID;
                    break;
                default:
                    // TODO: エラー設計お願いします
                    throw Error();
            }

            this.roomID = roomID;

            firebase.database().ref(`/messageRooms/${roomID}`).once('value')
                .then((snapshot: any):  void => {
                    const membersJSON: any = snapshot.val().members;
                    this.members = Object.keys(membersJSON).map((key: string): Member => {
                        return Member.fromRTDB(membersJSON[key]);
                    });

                    const messagesJSON: any = snapshot.val().messages;
                    if(!messagesJSON){
                        this.messages = [];
                    } else {
                        this.messages = Object.keys(messagesJSON).map((key: string): Message => {
                            const resConvert: MessageConvertResponse = MessageConvertResponse.fromRTDB(key, this.roomID, messagesJSON[key]);
                            return Message.from(resConvert);
                        });
                    }

                    resolve(this.messages);
                })
                .catch((error: Error): void => {
                    reject(error);
                });
        });
    };

    // メッセージを同期します。
    // メッセージはすでにあるものも接続時に流れてくるので、usecaseでmessageを管理して重複排除します。
    public observeMessage = (callback: (message: Message) => void): void => {
        firebase.database().ref(`/messageRooms/${this.roomID}/messages`).on('child_added', (data: any): void => {

            const messagesJSON: any = data.val();
            const resConvert: MessageConvertResponse = MessageConvertResponse.fromRTDB(data.key, this.roomID, messagesJSON);
            const message: Message = Message.from(resConvert);

            const finded: Message | undefined = this.messages.find((m: Message): boolean => { 
                return m.id === data.key;
            });

            if (finded) {
                return;
            }

            this.messages.push(message);
            callback(message);
        });
    };

    // メッセージを送信します。
    // observeMessageに流れてくるので返り値はvoidです。
    public sendMessage = (user: User, text: string, imageURL: string, fileURL: string, toId: string): Promise<void> => {
        return new Promise<void>((resolve, reject) => {
            let myID: string;
            let fromCompany: boolean;
            switch (user.role) {
                case Role.Role_Customer:
                    if (!user.customer) {
                        throw Error();
                    }
                    myID = user.customer.id;
                    fromCompany = false;
                    break;
                case Role.Role_Client:
                    if (!user.client) {
                        throw Error();
                    }
                    myID = user.client.companyID;
                    fromCompany = true;
                    break;
                default:
                    // TODO: エラー設計お願いします
                    throw Error();
            }

            const now: number = Date.now();

            const ref: firebase.database.ThenableReference = firebase.database().ref(`/messageRooms/${this.roomID}/messages`).push();

            resolve(ref.set({
                fromId: myID,
                toId,
                fromCompany,
                text,
                imageUrl: imageURL,
                fileUrl: fileURL,
                createdAt: now,
            }));
        });

    };

    public closeRoom = () => {
        return firebase.database().ref(`/messageRooms/${this.roomID}/messages`).off()
    };

    public getMembers = (): Member[] => {
        return this.members;
    }
}

const createChatUsecase = (): IChatUseCase => {
    return new ChatUseCase();
};

export {
    IChatUseCase, createChatUsecase
}


// WEBPACK FOOTER //
// ./src/domain/usecase/chat_usecase.ts